import type { Country } from '../constants';
import { Currency, CURRENCY_KEY, COUNTRY_KEY, CURRENCY_DECIMALS, CURRENCY_INFO } from '../constants';
import Cookies from 'js-cookie';

export const getCurrencyCookie = () => {
  return Cookies.get(CURRENCY_KEY) as Currency | undefined;
};
export const getCountryCookie = () => {
  return Cookies.get(COUNTRY_KEY) as Country | undefined;
};

export const setCurrencyCookie = (currency: Currency) => {
  Cookies.set(CURRENCY_KEY, currency);
};
export const setCountryCookie = (country: Country) => {
  Cookies.set(COUNTRY_KEY, country);
};

/**
 * Formats amount with the appropriate currency symbol based on the country.
 * @param {string} country - The name of the country.
 * @param {number|string} amount - The amount to format.
 * @returns {string} - The formatted amount with currency symbol.
 */

export function formatCurrency(country: Currency, amount: number | string): string {
  const decimals = CURRENCY_DECIMALS[country] ?? 2;
  const formattedAmount = Number(amount).toFixed(decimals);

  if (!CURRENCY_INFO[country]) {
    return formattedAmount.toString();
  }

  const { symbol, position } = CURRENCY_INFO[country];

  if (position === 'prefix') {
    return `${symbol}${formattedAmount}`;
  } else {
    return `${formattedAmount} ${symbol}`;
  }
}

/** COP cart totals omit insignificant decimals unless an item price requests them. */
export function formatCartCurrency(currency: Currency, amount: number, preserveDecimals = false): string {
  if (currency !== Currency.Colombia) {
    return formatCurrency(currency, amount);
  }
  return `${CURRENCY_INFO[currency].symbol}${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: preserveDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount)}`;
}

/**
 * Formats a loyalty credit amount using the server-provided fractionDigits
 * (0 for JPY/CLP, 2 for most other currencies) and the currency symbol.
 */
export function formatLoyaltyCredit(currency: Currency, amount: number, fractionDigits: number): string {
  const formattedAmount = Number(amount).toFixed(fractionDigits);

  if (!CURRENCY_INFO[currency]) {
    return formattedAmount;
  }

  const { symbol, position } = CURRENCY_INFO[currency];

  if (position === 'prefix') {
    return `${symbol}${formattedAmount}`;
  } else {
    return `${formattedAmount} ${symbol}`;
  }
}
